import { useState } from 'react';
import PropTypes from 'prop-types';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { projectData } from '../utils/constants';
import { SectionHeading } from './FxSections';

const isRealLink = (url) => Boolean(url) && url !== '#';

const getInitials = (title) =>
    title
        .split(' ')
        .filter((word) => /^[A-Z]/.test(word))
        .slice(0, 2)
        .map((word) => word[0])
        .join('');

function ProjectMedia({ title, image }) {
    const [failed, setFailed] = useState(!image);
    if (failed) {
        return (
            <div className="fx-project__media fx-project__media--fallback" aria-hidden="true">
                <span className="fx-project__monogram">{getInitials(title)}</span>
                <AutoAwesomeIcon className="fx-project__spark" />
            </div>
        );
    }
    return (
        <div className="fx-project__media">
            <img
                src={image}
                alt={`Screenshot of ${title}`}
                loading="lazy"
                decoding="async"
                onError={() => setFailed(true)}
            />
        </div>
    );
}

ProjectMedia.propTypes = {
    title: PropTypes.string.isRequired,
    image: PropTypes.string,
};

function ProjectLinks({ title, demo, github }) {
    if (!isRealLink(demo) && !isRealLink(github)) {
        return <p className="fx-project__note">Enterprise / internal project</p>;
    }
    return (
        <div className="fx-project__links">
            {isRealLink(demo) && (
                <a className="fx-btn fx-btn--small fx-btn--primary" href={demo} target="_blank" rel="noopener noreferrer" aria-label={`Live demo of ${title}`}>
                    <LaunchIcon fontSize="inherit" aria-hidden="true" /> Live demo
                </a>
            )}
            {isRealLink(github) && (
                <a className="fx-btn fx-btn--small fx-btn--ghost" href={github} target="_blank" rel="noopener noreferrer" aria-label={`Source code of ${title} on GitHub`}>
                    <GitHubIcon fontSize="inherit" aria-hidden="true" /> Source
                </a>
            )}
        </div>
    );
}

ProjectLinks.propTypes = {
    title: PropTypes.string.isRequired,
    demo: PropTypes.string,
    github: PropTypes.string,
};

const setTilt = (event) => {
    const card = event.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    card.style.setProperty('--mx', `${x * 100}%`);
    card.style.setProperty('--my', `${y * 100}%`);
    card.style.setProperty('--ry', `${(x - 0.5) * 8}deg`);
    card.style.setProperty('--rx', `${(0.5 - y) * 8}deg`);
};

const resetTilt = (event) => {
    const card = event.currentTarget;
    card.style.setProperty('--ry', '0deg');
    card.style.setProperty('--rx', '0deg');
};

function ProjectCard({ project, featured }) {
    return (
        <article
            className={`fx-glass fx-project${featured ? ' fx-project--featured' : ''}`}
            onPointerMove={setTilt}
            onPointerLeave={resetTilt}
            data-reveal
        >
            <ProjectMedia title={project.title} image={project.image} />
            <div className="fx-project__body">
                <h3 className="fx-project__title">{project.title}</h3>
                <p className="fx-project__desc">{project.description}</p>
                {project.highlights && (
                    <ul className="fx-project__highlights">
                        {project.highlights.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                )}
                <ul className="fx-chips" aria-label="Technologies">
                    {project.technologies.map((tech) => (
                        <li key={tech} className="fx-chip">{tech}</li>
                    ))}
                </ul>
                <ProjectLinks title={project.title} demo={project.demo} github={project.github} />
            </div>
        </article>
    );
}

ProjectCard.propTypes = {
    project: PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        technologies: PropTypes.arrayOf(PropTypes.string).isRequired,
        image: PropTypes.string,
        demo: PropTypes.string,
        github: PropTypes.string,
        highlights: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    featured: PropTypes.bool,
};

export default function Projects() {
    return (
        <section id="projects" className="fx-section" aria-labelledby="projects-title">
            <div className="fx-container">
                <SectionHeading id="projects-title" index="04" kicker="Selected work" title={<>Projects that <span className="fx-gradient-text">ship impact</span></>}>
                    From a multi-tenant platform serving 200K+ users to AI
                    tooling and award-winning hackathon builds.
                </SectionHeading>
                <div className="fx-projects">
                    {projectData.map((project, position) => (
                        <ProjectCard key={project.title} project={project} featured={position === 0} />
                    ))}
                </div>
            </div>
        </section>
    );
}
